/**
 * NegativeCard — criticità singola del report AI con piano d'azione editabile
 * dall'Area Manager e bottone "Salva come piano d'azione" (crea una nota
 * nello Store Notes panel).
 *
 * Estratto da `AIInsightsPanel.js` il 2026-05-29 per ridurre la complessità
 * ciclomatica del file principale. API/data-testid invariati.
 */
import React, { useEffect, useState } from 'react';
import {
  AlertCircle, Lightbulb, Bookmark, BookmarkCheck,
} from 'lucide-react';
import { aiApi, storeNotesApi } from '../lib/api';


function buildNoteText(negative, savedText, actionText) {
  const lines = [];
  lines.push(`[Piano d'azione AI] ${negative.title}`);
  lines.push('');
  if (negative.description) lines.push(negative.description);
  if (negative.suggestion) {
    lines.push('');
    lines.push(`Suggerimento operativo: ${negative.suggestion}`);
  }
  const plan = (savedText || actionText || '').trim();
  if (plan) {
    lines.push('');
    lines.push(`Piano: ${plan}`);
  }
  return lines.join('\n');
}


export function AIInsightsNegativeCard({
  index, negative, isOps, scope, entityId, storeId, signature, existingAction,
}) {
  const [actionText, setActionText] = useState(existingAction?.text || '');
  const [savedText, setSavedText] = useState(existingAction?.text || '');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveOk, setSaveOk] = useState(false);

  const [pinning, setPinning] = useState(false);
  const [pinError, setPinError] = useState('');
  const [pinnedNoteId, setPinnedNoteId] = useState(null);
  const canPin = scope === 'store' && !!storeId;

  // Reset locale quando la "fonte di verità" cambia (signature/index/existingAction).
  useEffect(() => {
    setActionText(existingAction?.text || '');
    setSavedText(existingAction?.text || '');
    setPinnedNoteId(null);
  }, [existingAction?.text, signature, index]);

  const dirty = actionText !== savedText;

  const pinAsActionPlan = async () => {
    if (!canPin) return;
    setPinning(true); setPinError('');
    try {
      const text = buildNoteText(negative, savedText, actionText);
      const res = await storeNotesApi.create(storeId, text, { source: 'ai_action_plan' });
      setPinnedNoteId(res.data?.note_id || 'pinned');
    } catch (e) {
      setPinError(e.response?.data?.detail || e.message || 'Errore salvataggio');
    } finally { setPinning(false); }
  };

  const save = async () => {
    if (!scope || !entityId || !signature) return;
    setSaving(true); setSaveError(''); setSaveOk(false);
    try {
      await aiApi.saveAction(scope, entityId, index, { signature, text: actionText });
      setSavedText(actionText);
      setSaveOk(true);
      setTimeout(() => setSaveOk(false), 2000);
    } catch (e) {
      setSaveError(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="border border-red-200 bg-red-50/40 p-4" data-testid={`ai-negative-${index}`}>
      <div className="flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-red-700 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-red-900 leading-tight">{negative.title}</p>
          <p className="text-xs text-slate-700 mt-1 leading-snug">{negative.description}</p>

          {isOps && negative.suggestion && (
            <div className="mt-3 pt-3 border-t border-red-200/60 flex items-start gap-2" data-testid={`ai-suggestion-${index}`}>
              <Lightbulb className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-[10px] uppercase tracking-wider font-semibold text-amber-800">Suggerimento operativo (OPS)</p>
                <p className="text-xs text-slate-700 mt-0.5 leading-snug">{negative.suggestion}</p>
              </div>
            </div>
          )}

          <div className="mt-3">
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 block mb-1">
              Piano d'azione (Area Manager)
            </label>
            <textarea
              value={actionText}
              onChange={(e) => setActionText(e.target.value)}
              onBlur={() => { if (dirty) save(); }}
              placeholder="Come intendi operare su questa criticità? (premi Tab o clicca fuori per salvare)"
              className="w-full text-xs border border-slate-200 focus:border-slate-900 focus:outline-none px-2 py-1.5 rounded-none bg-white min-h-[60px] resize-y"
              data-testid={`ai-action-textarea-${index}`}
            />
            <div className="flex items-center gap-3 mt-1 text-[11px] min-h-[16px]">
              {saving && <span className="text-slate-500">Salvataggio...</span>}
              {!saving && saveOk && <span className="text-emerald-700">✓ Salvato</span>}
              {!saving && !saveOk && dirty && <span className="text-amber-700">Modifica non salvata</span>}
              {!saving && !saveOk && !dirty && savedText && existingAction && (
                <span className="text-slate-400">
                  Salvato da {existingAction.author_name} il {new Date(existingAction.updated_at || existingAction.created_at).toLocaleDateString('it-IT')}
                </span>
              )}
              {saveError && <span className="text-red-600">Errore: {saveError}</span>}
            </div>

            {canPin && (
              <div
                className="flex items-center gap-2 mt-2 pt-2 border-t border-red-200/40"
                data-testid={`ai-pin-row-${index}`}
              >
                <button
                  type="button"
                  onClick={pinAsActionPlan}
                  disabled={pinning || !!pinnedNoteId}
                  className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 border transition-colors ${
                    pinnedNoteId
                      ? 'bg-emerald-50 text-emerald-800 border-emerald-300 cursor-default'
                      : 'bg-white text-slate-700 border-slate-300 hover:border-slate-900 hover:bg-slate-50'
                  } disabled:opacity-60`}
                  data-testid={`ai-pin-action-${index}`}
                  title={pinnedNoteId ? 'Già salvato come nota su questo store' : 'Crea una nota nel pannello Note del negozio'}
                >
                  {pinnedNoteId ? (
                    <BookmarkCheck className="w-3.5 h-3.5" />
                  ) : (
                    <Bookmark className="w-3.5 h-3.5" />
                  )}
                  {pinning
                    ? 'Salvataggio…'
                    : pinnedNoteId
                    ? 'Salvato come piano d\u2019azione'
                    : 'Salva come piano d\u2019azione'}
                </button>
                {pinError && <span className="text-[11px] text-red-600">{pinError}</span>}
                {pinnedNoteId && !pinError && (
                  <span className="text-[11px] text-emerald-700">Vai alla tab Note per vederla</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
